import React, { Suspense, useMemo } from "react";
import { cn } from "@/lib/utils";
import { ProjectPageNav } from "@/store/zustand/layoutStore";
import { getNavigationItem } from "./navigationConfig";
import { Project } from "@/utils/types";
import { useViews } from "@/components/views/hooks/useViews";
import Loading from "../ui/loading";

interface PanelRendererProps {
  activeNav: ProjectPageNav | null;
  currentProject: Project | null;
  selectedView: string | null;
  onViewSelect: (viewId: string) => void;
  activeSettingsTab?: any;
  onSettingsTabChange?: (tab: any) => void;
}

export const PanelRenderer: React.FC<PanelRendererProps> = React.memo(
  ({
    activeNav,
    currentProject,
    selectedView,
    onViewSelect,
    activeSettingsTab,
    onSettingsTabChange,
  }) => {
    const { getViews } = useViews();

    const navItem = useMemo(() => {
      if (!activeNav) return null;
      return getNavigationItem(activeNav);
    }, [activeNav]);

    const panelProps = useMemo(() => {
      if (!navItem) return {};

      if (navItem.id === ProjectPageNav.Views) {
        return {
          selectedViewId: selectedView,
          onViewSelect: onViewSelect,
        };
      }

      if (navItem.id === ProjectPageNav.Settings) {
        return {
          activeTab: activeSettingsTab,
          onTabChange: onSettingsTabChange,
        };
      }

      return {};
    }, [
      navItem,
      selectedView,
      onViewSelect,
      activeSettingsTab,
      onSettingsTabChange,
    ]);

    const PanelComponent = useMemo(() => {
      return navItem?.panelComponent;
    }, [navItem]);

    const panelClassName = useMemo(() => {
      if (!navItem?.panelWidth) return "border-r h-full border-secondary-n-900";
      return cn(navItem.panelWidth, "border-r h-full border-secondary-n-900");
    }, [navItem?.panelWidth]);

    if (!activeNav || !navItem || !PanelComponent) {
      return null;
    }

    // For Views panel, check if there are no views before rendering
    if (navItem.id === ProjectPageNav.Views && currentProject?.id) {
      const views = getViews(currentProject.id);
      if (views.length === 0) {
        return null;
      }
    }

    return (
      <div className={panelClassName}>
        <Suspense
          fallback={
            <div className="p-4">
              <Loading />
            </div>
          }
        >
          <PanelComponent {...panelProps} />
        </Suspense>
      </div>
    );
  },
);

PanelRenderer.displayName = "PanelRenderer";
